import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, setDoc, getDocs, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { Database, CheckCircle, AlertCircle, Loader, RefreshCw } from 'lucide-react';

const SeedData = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState({ success: [], errors: [] });
  const [dataStatus, setDataStatus] = useState(null);

  // التحقق من البيانات الموجودة في Firebase
  const checkExistingData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [categoriesSnapshot, productsSnapshot, servicesSnapshot, modificationsSnapshot] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.CATEGORIES)),
        getDocs(collection(db, COLLECTIONS.PRODUCTS)),
        getDocs(collection(db, COLLECTIONS.SERVICES)),
        getDocs(collection(db, COLLECTIONS.MODIFICATIONS))
      ]);

      const status = {
        categories: categoriesSnapshot.size,
        products: productsSnapshot.size,
        services: servicesSnapshot.size,
        modifications: modificationsSnapshot.size
      };

      setDataStatus(status);
      console.log('📊 Current Firebase Data:', status);
    } catch (error) {
      console.error('Error checking data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    checkExistingData();
  }, []);

  // بيانات الفئات
  const categoriesData = [
    {
      name: 'اكسسوارات داخلية',
      description: 'إكسسوارات داخلية فاخرة ومريحة',
      icon: '💺',
      color: 'purple',
      active: true
    },
    {
      name: 'اكسسوارات خارجية',
      description: 'إكسسوارات خارجية أنيقة',
      icon: '🚗',
      color: 'blue',
      active: true
    },
    {
      name: 'قطع غيار محرك',
      description: 'قطع غيار أصلية عالية الجودة',
      icon: '⚙️',
      color: 'red',
      active: true
    },
    {
      name: 'أنظمة صوت وإلكترونيات',
      description: 'أنظمة صوت وتقنيات متقدمة',
      icon: '🔊',
      color: 'yellow',
      active: true
    },
    {
      name: 'العناية بالسيارة',
      description: 'منتجات العناية والتنظيف',
      icon: '✨',
      color: 'green',
      active: true
    }
  ];

  // منتجات واقعية مع صور من Unsplash
  const productsData = [
    // اكسسوارات داخلية
    {
      name: 'حامل موبايل مغناطيسي فاخر',
      price: '89 ريال',
      brand: 'Baseus',
      stock: 'متوفر',
      description: 'حامل موبايل مغناطيسي قوي للسيارة، تصميم أنيق من الألمنيوم، تثبيت قوي على فتحة المكيف',
      categoryName: 'اكسسوارات داخلية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=800&q=80',
      features: ['مغناطيس قوي', 'دوران 360 درجة', 'تصميم ألومنيوم', 'لجميع الهواتف']
    },
    {
      name: 'عطر سيارة فاخر - Oud',
      price: '120 ريال',
      brand: 'Aromatherapy',
      stock: 'متوفر',
      description: 'عطر سيارة فاخر برائحة العود الأصلي، يدوم لفترة طويلة، تصميم أنيق وعصري',
      categoryName: 'اكسسوارات داخلية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1588405748880-12d1d2a59c75?w=800&q=80',
      features: ['رائحة العود الفاخر', 'يدوم 60 يوم', 'تصميم أنيق', 'آمن وطبيعي']
    },
    {
      name: 'إضاءة LED داخلية RGB',
      price: '149 ريال',
      brand: 'Govee',
      stock: 'متوفر',
      description: 'إضاءة LED داخلية متعددة الألوان مع تحكم عن طريق التطبيق، 16 مليون لون',
      categoryName: 'اكسسوارات داخلية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1617886322207-d759a409f5f2?w=800&q=80',
      features: ['16 مليون لون', 'تحكم بالتطبيق', 'مزامنة مع الموسيقى', 'سهل التركيب']
    },
    {
      name: 'أغطية مقاعد جلد طبيعي',
      price: '899 ريال',
      brand: 'FH Group',
      stock: 'متوفر',
      description: 'أغطية مقاعد من الجلد الطبيعي الفاخر، مريحة وأنيقة، متوافقة مع جميع السيارات',
      categoryName: 'اكسسوارات داخلية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80',
      features: ['جلد طبيعي 100%', 'تصميم فاخر', 'سهل التنظيف', 'مقاوم للماء']
    },
    {
      name: 'مخدة رقبة memory foam',
      price: '79 ريال',
      brand: 'Everlasting Comfort',
      stock: 'متوفر',
      description: 'مخدة رقبة من الميموري فوم الطبي، تصميم مريح يخفف آلام الرقبة أثناء القيادة',
      categoryName: 'اكسسوارات داخلية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=800&q=80',
      features: ['Memory Foam', 'غطاء قابل للغسل', 'تصميم أرجونومي', 'راحة فائقة']
    },
    {
      name: 'شاحن سيارة سريع USB-C',
      price: '65 ريال',
      brand: 'Anker',
      stock: 'متوفر',
      description: 'شاحن سيارة سريع بمنفذين USB-C، قوة 45 واط، شحن سريع لجميع الأجهزة',
      categoryName: 'اكسسوارات داخلية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1591290619762-a03c0f46b1ab?w=800&q=80',
      features: ['شحن سريع 45W', 'منفذين USB-C', 'حماية متعددة', 'متوافق مع الكل']
    },

    // اكسسوارات خارجية
    {
      name: 'كاميرا خلفية HD للسيارة',
      price: '249 ريال',
      brand: 'AUTO-VOX',
      stock: 'متوفر',
      description: 'كاميرا خلفية عالية الدقة HD، رؤية ليلية، مقاومة للماء، سهلة التركيب',
      categoryName: 'اكسسوارات خارجية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      features: ['دقة HD', 'رؤية ليلية', 'مقاومة للماء IP68', 'زاوية واسعة']
    },
    {
      name: 'مصابيح ضباب LED',
      price: '189 ريال',
      brand: 'Philips',
      stock: 'متوفر',
      description: 'مصابيح ضباب LED عالية السطوع، قوة 6000K، عمر طويل، سهلة التركيب',
      categoryName: 'اكسسوارات خارجية',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80',
      features: ['LED قوية', 'عمر 50,000 ساعة', 'مقاومة للصدمات', 'توفير طاقة']
    },

    // قطع غيار محرك
    {
      name: 'فلتر هواء K&N عالي الأداء',
      price: '459 ريال',
      brand: 'K&N',
      stock: 'متوفر',
      description: 'فلتر هواء قابل للغسيل وإعادة الاستخدام، يزيد من قوة المحرك وكفاءة الوقود',
      categoryName: 'قطع غيار محرك',
      type: 'part',
      imageUrl: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80',
      features: ['قابل للغسيل', 'زيادة القوة', 'كفاءة وقود', 'ضمان مليون ميل']
    },
    {
      name: 'شمعات إشعال NGK Iridium',
      price: '120 ريال',
      brand: 'NGK',
      stock: 'متوفر',
      description: 'شمعات إشعال إيريديوم عالية الأداء، عمر طويل، احتراق أفضل',
      categoryName: 'قطع غيار محرك',
      type: 'part',
      imageUrl: 'https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=800&q=80',
      features: ['Iridium', 'عمر 100,000 كم', 'أداء ممتاز', 'اقتصادية']
    },
    {
      name: 'زيت محرك Castrol Edge 5W-30',
      price: '185 ريال',
      brand: 'Castrol',
      stock: 'متوفر',
      description: 'زيت محرك صناعي كامل، حماية قصوى للمحرك، مناسب للسيارات الحديثة',
      categoryName: 'قطع غيار محرك',
      type: 'part',
      imageUrl: 'https://images.unsplash.com/photo-1621939514649-280e2ee25f60?w=800&q=80',
      features: ['صناعي كامل', 'حماية قصوى', 'كفاءة عالية', 'لجميع الأجواء']
    },
    {
      name: 'بطارية Bosch S5 AGM',
      price: '650 ريال',
      brand: 'Bosch',
      stock: 'متوفر',
      description: 'بطارية عالية الأداء تقنية AGM، مناسبة للسيارات بنظام Start-Stop',
      categoryName: 'قطع غيار محرك',
      type: 'part',
      imageUrl: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80',
      features: ['تقنية AGM', 'عمر طويل', 'شحن سريع', 'مقاومة للهزات']
    },

    // أنظمة صوت
    {
      name: 'سماعات JBL 6.5 بوصة',
      price: '450 ريال',
      brand: 'JBL',
      stock: 'متوفر',
      description: 'سماعات سيارة JBL عالية الجودة، صوت نقي وباص قوي، سهلة التركيب',
      categoryName: 'أنظمة صوت وإلكترونيات',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1545127398-14699f92334b?w=800&q=80',
      features: ['قوة 300 واط', 'صوت نقي', 'باص قوي', 'تصميم أنيق']
    },
    {
      name: 'شاشة أندرويد 10 بوصة',
      price: '1,299 ريال',
      brand: 'Pioneer',
      stock: 'متوفر',
      description: 'شاشة سيارة أندرويد، GPS، CarPlay، Android Auto، بلوتوث',
      categoryName: 'أنظمة صوت وإلكترونيات',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      features: ['شاشة 10 بوصة', 'CarPlay', 'Android Auto', 'GPS مدمج']
    },

    // العناية بالسيارة
    {
      name: 'طقم تنظيف سيارة كامل',
      price: '199 ريال',
      brand: 'Meguiar\'s',
      stock: 'متوفر',
      description: 'طقم تنظيف شامل للسيارة، شامبو، ملمع، حماية، فوط ميكروفايبر',
      categoryName: 'العناية بالسيارة',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?w=800&q=80',
      features: ['طقم كامل', 'جودة عالية', 'آمن على الطلاء', 'نتائج احترافية']
    },
    {
      name: 'ملمع ومانع تسرب Ceramic',
      price: '259 ريال',
      brand: 'Turtle Wax',
      stock: 'متوفر',
      description: 'طلاء سيراميك للحماية والتلميع، حماية تدوم 12 شهر، لمعة رائعة',
      categoryName: 'العناية بالسيارة',
      type: 'accessory',
      imageUrl: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=800&q=80',
      features: ['حماية سيراميك', 'يدوم 12 شهر', 'لمعة قوية', 'مقاوم للماء']
    }
  ];

  // بيانات الخدمات
  const servicesData = [
    {
      name: 'صيانة دورية شاملة',
      price: '350 ريال',
      duration: 'ساعتان',
      description: 'صيانة دورية شاملة تشمل فحص شامل وتغيير الزيت والفلتر',
      icon: '🔧',
      includes: [
        'فحص شامل للمحرك',
        'تغيير زيت المحرك',
        'تغيير فلتر الزيت',
        'فحص الفرامل',
        'فحص الإطارات',
        'فحص البطارية'
      ],
      active: true
    },
    {
      name: 'تغيير زيت وفلتر',
      price: '180 ريال',
      duration: '30 دقيقة',
      description: 'تغيير زيت المحرك والفلتر بزيت عالي الجودة',
      icon: '🛢️',
      includes: [
        'تغيير زيت المحرك',
        'تغيير فلتر الزيت',
        'فحص مستويات السوائل'
      ],
      active: true
    },
    {
      name: 'فحص شامل للمركبة',
      price: '150 ريال',
      duration: 'ساعة',
      description: 'فحص شامل لجميع أنظمة المركبة',
      icon: '🔍',
      includes: [
        'فحص المحرك',
        'فحص الفرامل',
        'فحص نظام التعليق',
        'فحص الإطارات'
      ],
      active: true
    }
  ];

  // بيانات التعديلات
  const modificationsData = [
    {
      name: 'تعديل نظام العادم',
      price: 'يبدأ من 3,500 ريال',
      description: 'تعديل شامل لنظام العادم لتحسين الأداء والصوت',
      icon: '💨',
      type: 'exhaust',
      features: ['زيادة القوة 15-20%', 'صوت رياضي', 'تقليل الوزن'],
      benefits: ['أداء محسّن', 'استهلاك وقود أفضل'],
      warranty: 'ضمان سنة واحدة',
      duration: '4-6 ساعات',
      active: true
    },
    {
      name: 'رفع أداء المحرك (Chip Tuning)',
      price: 'يبدأ من 2,500 ريال',
      description: 'رفع أداء المحرك عبر تعديل برمجة الكمبيوتر',
      icon: '⚡',
      type: 'performance',
      features: ['زيادة القوة 30-40%', 'زيادة العزم', 'تحسين التسارع'],
      benefits: ['أداء أقوى', 'تسارع أفضل'],
      warranty: 'ضمان سنة واحدة',
      duration: '2-3 ساعات',
      active: true
    }
  ];

  const seedData = async () => {
    setLoading(true);
    setProgress('جاري التحقق من Firebase...');
    setResults({ success: [], errors: [] });

    const successList = [];
    const errorList = [];

    try {
      const testDoc = await addDoc(collection(db, COLLECTIONS.TEST), { test: true });
      await deleteDoc(doc(db, 'test', testDoc.id));
      console.log('✅ Firebase connection OK');
    } catch (error) {
      errorList.push(`خطأ في الاتصال بـ Firebase: ${error.message}`);
      setLoading(false);
      setResults({ success: [], errors: errorList });
      return;
    }

    try {
      // 1. إضافة الفئات
      setProgress('جاري إضافة الفئات...');
      const categoryIds = {};
      let categoriesAdded = 0;

      for (const category of categoriesData) {
        try {
          const categoryData = {
            name: category.name,
            description: category.description,
            icon: category.icon,
            color: category.color,
            active: true,
            createdAt: Timestamp.now()
          };

          const docRef = await addDoc(collection(db, COLLECTIONS.CATEGORIES), categoryData);
          categoryIds[category.name] = docRef.id;
          categoriesAdded++;

          successList.push(`✅ تم إضافة الفئة: ${category.name}`);
        } catch (error) {
          errorList.push(`❌ خطأ في إضافة الفئة "${category.name}": ${error.message}`);
        }
      }

      setProgress(`تم إضافة ${categoriesAdded} فئة`);

      // 2. إضافة المنتجات مع الصور
      setProgress('جاري إضافة المنتجات مع الصور...');
      let productsAdded = 0;

      for (const product of productsData) {
        try {
          setProgress(`جاري إضافة منتج: ${product.name}...`);

          // استخدام رابط الصورة المباشر من Unsplash
          const imageUrl = product.imageUrl;

          const categoryId = categoryIds[product.categoryName] || null;

          const productData = {
            name: product.name,
            price: product.price,
            brand: product.brand || '',
            stock: product.stock || 'متوفر',
            description: product.description || '',
            categoryName: product.categoryName,
            categoryId: categoryId,
            type: product.type || 'accessory',
            image: imageUrl,
            features: product.features || [],
            createdAt: Timestamp.now()
          };

          const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
          productsAdded++;

          successList.push(`✅ تم إضافة المنتج: ${product.name}`);
        } catch (error) {
          errorList.push(`❌ خطأ في إضافة المنتج "${product.name}": ${error.message}`);
        }
      }

      setProgress(`تم إضافة ${productsAdded} منتج`);

      // 3. إضافة الخدمات
      setProgress('جاري إضافة الخدمات...');
      let servicesAdded = 0;

      for (const service of servicesData) {
        try {
          const serviceData = {
            name: service.name,
            price: service.price,
            duration: service.duration || '',
            description: service.description || '',
            icon: service.icon || '🔧',
            includes: service.includes || [],
            active: true,
            createdAt: Timestamp.now()
          };

          const docRef = await addDoc(collection(db, COLLECTIONS.SERVICES), serviceData);
          servicesAdded++;

          successList.push(`✅ تم إضافة الخدمة: ${service.name}`);
        } catch (error) {
          errorList.push(`❌ خطأ في إضافة الخدمة "${service.name}": ${error.message}`);
        }
      }

      setProgress(`تم إضافة ${servicesAdded} خدمة`);

      // 4. إضافة التعديلات
      setProgress('جاري إضافة التعديلات...');
      let modificationsAdded = 0;

      for (const modification of modificationsData) {
        try {
          const modData = {
            name: modification.name,
            price: modification.price,
            description: modification.description || '',
            icon: modification.icon || '⚙️',
            type: modification.type || 'performance',
            features: modification.features || [],
            benefits: modification.benefits || [],
            warranty: modification.warranty || '',
            duration: modification.duration || '',
            active: true,
            createdAt: Timestamp.now()
          };

          const docRef = await addDoc(collection(db, COLLECTIONS.MODIFICATIONS), modData);
          modificationsAdded++;

          successList.push(`✅ تم إضافة التعديل: ${modification.name}`);
        } catch (error) {
          errorList.push(`❌ خطأ في إضافة التعديل "${modification.name}": ${error.message}`);
        }
      }

      setProgress(`تم إضافة ${modificationsAdded} تعديل`);

      const totalAdded = categoriesAdded + productsAdded + servicesAdded + modificationsAdded;
      setProgress(`✅ تم الانتهاء! تم إضافة ${totalAdded} عنصر بنجاح`);
      setResults({ success: successList, errors: errorList });

      await checkExistingData(true);

      if (totalAdded > 0) {
        alert(`✅ تم إضافة البيانات بنجاح!\\n\\n- ${categoriesAdded} فئة\\n- ${productsAdded} منتج مع صور\\n- ${servicesAdded} خدمة\\n- ${modificationsAdded} تعديل`);
      }
    } catch (error) {
      errorList.push(`خطأ عام: ${error.message}`);
      setResults({ success: successList, errors: errorList });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">إضافة منتجات واقعية</h1>
        <p className="text-gray-600 mt-2">
          منتجات حقيقية مع صور واقعية من AliExpress وAmazon
        </p>
      </div>

      <div className="bg-blue-50 border-r-4 border-blue-500 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-blue-800 font-medium mb-1">ملاحظة مهمة:</p>
            <p className="text-blue-700 text-sm mb-2">
              المنتجات تحتوي على صور واقعية من Unsplash (مجانية وعالية الجودة)
            </p>
            <p className="text-blue-600 text-xs">
              � الصور ستظهر مباشرة في الموقع بعد الإضافة
            </p>
          </div>
        </div>
      </div>

      {dataStatus && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">البيانات الحالية:</h2>
            <button
              onClick={checkExistingData}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              تحديث
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">الفئات</p>
              <p className="text-2xl font-bold text-blue-600">{dataStatus.categories}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">المنتجات</p>
              <p className="text-2xl font-bold text-green-600">{dataStatus.products}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">الخدمات</p>
              <p className="text-2xl font-bold text-yellow-600">{dataStatus.services}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">التعديلات</p>
              <p className="text-2xl font-bold text-purple-600">{dataStatus.modifications}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-4 mb-6">
          <Database className="w-8 h-8 text-yellow-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">منتجات واقعية ستُضاف:</h2>
            <ul className="text-sm text-gray-600 mt-2 space-y-1">
              <li>• {categoriesData.length} فئة</li>
              <li>• {productsData.length} منتج واقعي مع صور</li>
              <li>• {servicesData.length} خدمة</li>
              <li>• {modificationsData.length} تعديل</li>
            </ul>
          </div>
        </div>

        <button
          onClick={seedData}
          disabled={loading}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold py-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              {progress}
            </>
          ) : (
            <>
              <Database className="w-5 h-5" />
              إضافة المنتجات مع الصور
            </>
          )}
        </button>

        {progress && !loading && (
          <div className="text-center mt-4">
            <p className="text-gray-700 font-medium">{progress}</p>
          </div>
        )}
      </div>

      {(results.success.length > 0 || results.errors.length > 0) && (
        <div className="space-y-4">
          {results.success.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-green-900">
                  نجح ({results.success.length})
                </h3>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {results.success.map((msg, idx) => (
                  <p key={idx} className="text-sm text-green-700">
                    {msg}
                  </p>
                ))}
              </div>
            </div>
          )}

          {results.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-red-900">
                  أخطاء ({results.errors.length})
                </h3>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {results.errors.map((msg, idx) => (
                  <p key={idx} className="text-sm text-red-700">
                    {msg}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeedData;
